/**
 * NotificationPromptModal
 *
 * A centered overlay modal that explains why notifications are important
 * and asks the user to enable them. Shown when:
 * - Permission is "default" (never asked) → first-time prompt
 * - Permission is "denied" → reminder that re-appears after cooldown
 *
 * Props:
 *   show        {boolean}  - Whether to render the modal
 *   isPrompting {boolean}  - Whether the browser dialog is currently open
 *   onEnable    {function} - Called when user clicks "Enable Notifications"
 *   onDismiss   {function} - Called when user dismisses the modal
 *   isDenied    {boolean}  - If true, shows the "blocked" variant copy
 */

import React from 'react';

export default function NotificationPromptModal({
  show,
  isPrompting,
  onEnable,
  onDismiss,
  isDenied,
}) {
  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: 20,
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #1a1f2e, #0f111a)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 24,
          padding: '32px 28px',
          maxWidth: 400,
          width: '100%',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          textAlign: 'center',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: 'rgba(255,255,255,0.05)',
            border: 'none',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            fontSize: 18,
            fontFamily: 'inherit',
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Icon */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: isDenied
              ? 'rgba(251, 191, 36, 0.15)'
              : 'rgba(99, 102, 241, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          {isDenied ? (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          )}
        </div>

        {/* Title */}
        <h2
          style={{
            margin: '0 0 8px',
            color: '#f1f5f9',
            fontSize: 20,
            fontWeight: 700,
            fontFamily: 'inherit',
            lineHeight: 1.3,
          }}
        >
          {isDenied ? 'Notifications Are Blocked' : 'Enable Notifications'}
        </h2>

        {/* Description */}
        <p
          style={{
            margin: '0 0 6px',
            color: '#94a3b8',
            fontSize: 14,
            lineHeight: 1.6,
            fontFamily: 'inherit',
          }}
        >
          {isDenied
            ? 'You\'ve blocked notifications for this site. Without them, you\'ll miss incoming messages when you\'re not actively in the chat.'
            : 'Dare2Chat needs notification access to alert you when you receive new messages. This is essential for real-time chat functionality.'}
        </p>

        {isDenied && (
          <p
            style={{
              margin: '0 0 20px',
              color: '#64748b',
              fontSize: 13,
              lineHeight: 1.5,
              fontFamily: 'inherit',
            }}
          >
            To enable, go to your browser settings → Site permissions →
            Notifications, and allow access for this site.
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={onEnable}
            disabled={isPrompting}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 12,
              border: 'none',
              background: isPrompting
                ? 'rgba(99, 102, 241, 0.4)'
                : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: isPrompting ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'opacity 0.2s',
              opacity: isPrompting ? 0.7 : 1,
            }}
          >
            {isPrompting ? (
              <>
                <span style={{
                  width: 16,
                  height: 16,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'd2c-spin 0.6s linear infinite',
                  display: 'inline-block',
                }} />
                Requesting...
              </>
            ) : isDenied ? (
              'Try Again'
            ) : (
              'Enable Notifications'
            )}
          </button>

          <button
            onClick={onDismiss}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 12,
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {isDenied ? 'Remind Me Later' : 'Skip for Now'}
          </button>
        </div>

        {/* Spinning animation keyframe injected via style tag */}
        <style>{`
          @keyframes d2c-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
